"use client";
import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

interface VideoPlayerProps {
    videoSource?: string;
    title?: string;
    description?: string;
    btnLabel?: string;
    btnLink?: string;
}

interface HeroVideo2Props {
    data?: {
        title?: string;
        subtitle?: string;
        btnLink?: string;
        btnText?: string;
        videos?: VideoPlayerProps[];
        isLive?: boolean;
    };
}

export default function HeroVideo2_Layout3({ data }: HeroVideo2Props) {
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const [isMuted, setIsMuted] = useState(true);

    const bannerVideoData = data?.videos?.[0] || null;
    const videoSources = data?.videos || [];

    useEffect(() => {
        videoRefs.current.forEach((video) => {
            if (video) {
                video.muted = true;
                // video.play().catch((e) => console.log("Autoplay prevented:", e));
            }
        });
    }, []);

    return (
        <section className="relative w-full min-h-screen bg-black overflow-hidden">
            {/* Hero Section with Title Over Image */}
            <div className="relative h-screen flex items-center justify-center">
                {bannerVideoData?.videoSource && (
                    <div className="absolute inset-0 z-0 overflow-hidden">
                        <video
                            className="w-full h-full object-cover scale-110"
                            autoPlay
                            loop
                            muted={isMuted}
                            playsInline
                        >
                            <source src={bannerVideoData.videoSource} type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/80"></div>
                    </div>
                )}

                <div className="relative z-10 text-center px-6">
                    <motion.h1
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="text-6xl md:text-8xl lg:text-9xl font-bold text-white mb-6"
                    >
                        {data?.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 1 }}
                        className="text-xl text-gray-300 max-w-2xl mx-auto font-light tracking-wider"
                    >
                        {data?.subtitle}
                    </motion.p>
                </div>

                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10">
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
                    >
                        <motion.div
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className="w-1 h-3 bg-white rounded-full mt-2"
                        ></motion.div>
                    </motion.div>
                </div>
            </div>

            {/* First Creative Collage */}
            {videoSources.length > 1 && (
                <div className="relative py-20 px-6">
                    <div className="max-w-7xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="text-4xl md:text-5xl text-center text-white mb-16 font-light"
                        >
                            Featured Moments
                        </motion.h2>

                        <div className="grid grid-cols-12 gap-5 md:gap-8">
                            {/* Large center video with clip path */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.7 }}
                                viewport={{ once: true }}
                                className="col-span-12 md:col-span-8 md:col-start-3 relative group h-96 md:h-[500px]"
                            >
                                {videoSources[1]?.videoSource && (
                                    <>
                                        <div className="absolute inset-0 clip-path-polygon-[50%_0%,100%_25%,100%_75%,50%_100%,0%_75%,0%_25%] overflow-hidden transform rotate-3 group-hover:rotate-0 transition-transform duration-700">
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                ref={(el) => (videoRefs.current[0] = el)}
                                            >
                                                <source src={videoSources[1].videoSource} type="video/mp4" />
                                            </video>
                                        </div>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                            <div className="text-center p-6">
                                                <h3 className="text-2xl text-white   mb-3">{videoSources[1]?.title}</h3>
                                                <div className="w-16 h-1 bg-white mx-auto my-4"></div>
                                                <p className="text-gray-200 mb-5 max-w-md">{videoSources[1]?.description}</p>
                                                {videoSources[1]?.btnLabel && (
                                                    <button className="px-6 py-2 border border-white text-white hover:bg-white hover:text-black transition-colors">
                                                        {videoSources[1]?.btnLabel}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </motion.div>

                            {/* Rotated side videos */}
                            {videoSources.slice(2, 4).map((video, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: index === 0 ? -50 : 50, rotate: index === 0 ? -5 : 5 }}
                                    whileInView={{ opacity: 1, x: 0, rotate: index === 0 ? -3 : 3 }}
                                    transition={{ duration: 0.7, delay: index * 0.2 }}
                                    viewport={{ once: true }}
                                    className={`col-span-6 md:col-span-4 relative group h-64 md:h-80 ${index === 0 ? 'md:-mt-32' : 'md:mt-32'}`}
                                >
                                    {video?.videoSource && (
                                        <>
                                            <div className="absolute inset-0 overflow-hidden transform transition-all duration-700 group-hover:rotate-0 group-hover:scale-105"
                                                style={{ rotate: index === 0 ? '-3deg' : '3deg' }}
                                            >
                                                <video
                                                    className="w-full h-full object-cover"
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                    ref={(el) => (videoRefs.current[index + 1] = el)}
                                                >
                                                    <source src={video.videoSource} type="video/mp4" />
                                                </video>
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                                                <div className="p-4 text-center w-full">
                                                    <h4 className="text-white text-sm md:text-base font-medium">{video.title}</h4>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Second Advanced Collage */}
            {videoSources.length > 4 && (
                <div className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
                    <div className="max-w-7xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="text-4xl md:text-5xl text-center text-white mb-16 font-light"
                        >
                            Explore More
                        </motion.h2>

                        <div className="relative">
                            {/* Main grid layout */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                                {videoSources.slice(4).map((video, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className={`relative group overflow-hidden ${getHeightClass(index)} ${getColumnSpan(index)}`}
                                        style={{ transform: getRotation(index) }}
                                    >
                                        {video?.videoSource && (
                                            <>
                                                <div className="absolute inset-0 overflow-hidden">
                                                    <video
                                                        className="w-full h-full object-cover"
                                                        autoPlay
                                                        loop
                                                        muted
                                                        playsInline
                                                        ref={(el) => (videoRefs.current[index + 4] = el)}
                                                    >
                                                        <source src={video.videoSource} type="video/mp4" />
                                                    </video>
                                                </div>

                                                {/* Blur overlay */}
                                                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                                    <div className="text-center p-4">
                                                        <h3 className="text-white text-lg font-medium mb-2">{video.title}</h3>
                                                        <p className="text-gray-200 text-sm mb-3">{video.description}</p>
                                                        {video.btnLabel && (
                                                            <button className="px-4 py-1 text-xs border border-white text-white hover:bg-white hover:text-black transition-colors">
                                                                {video.btnLabel}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </motion.div>
                                ))}
                            </div>

                            {/* Floating elements */}
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.5 }}
                                viewport={{ once: true }}
                                className="absolute -top-10 -right-10 w-40 h-40 rounded-full border border-white/20 flex items-center justify-center"
                            >
                                <div className="w-32 h-32 rounded-full border border-white/40 flex items-center justify-center">
                                    <div className="w-24 h-24 rounded-full border border-white/60 flex items-center justify-center">
                                        <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, delay: 0.7 }}
                                viewport={{ once: true }}
                                className="absolute -bottom-10 -left-10 w-32 h-32 clip-path-polygon-[50%_0%,100%_38%,82%_100%,18%_100%,0%_38%] bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-md"
                            ></motion.div>
                        </div>
                    </div>
                </div>
            )}

            {/* CTA Section */}
            {data?.btnLink && data?.btnText && (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                    className="py-20 px-6 text-center"
                >
                    <Link href={data.btnLink}>
                        <button className="relative px-10 py-5 bg-transparent border-2 border-white text-white text-lg font-light tracking-widest overflow-hidden group">
                            <span className="relative z-10">{data.btnText}</span>
                            <div className="absolute inset-0 bg-white transform -skew-x-12 -translate-x-full group-hover:translate-x-0 transition-transform duration-500 z-0"></div>
                        </button>
                    </Link>
                </motion.div>
            )}
        </section>
    );
}

// Helper functions for dynamic styling
function getHeightClass(index: number): string {
    const heightClasses = [
        'h-64', 'h-80', 'h-64',
        'h-72', 'h-96', 'h-80',
        'h-64', 'h-72', 'h-80'
    ];
    return heightClasses[index] || 'h-64';
}

function getColumnSpan(index: number): string {
    if (index === 4) return 'md:col-span-2';
    if (index === 5) return 'md:col-span-2';
    return '';
}

function getRotation(index: number): string {
    const rotations = [
        'rotate-2', '-rotate-2', 'rotate-3',
        '-rotate-1', 'rotate-1', '-rotate-2',
        'rotate-2', '-rotate-1', 'rotate-1'
    ];
    return rotations[index] || 'rotate-0';
}